import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperClass from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { Controller, Navigation, Pagination } from 'swiper/modules';

import { styled, Theme } from '@mui/material';

import { isClothing } from '@mewa/types';
import {
  FadeText,
  getARIATranslations,
  SliderButtonNext,
  SliderButtonPrev,
  SliderButtonStyle,
} from '@mewa/ui';

import Image from '../../../components/image';
import { ProductFallbackImage } from '../../../components/product-fallback-image';
import { jpgPath } from '../../../helpers/asset-factory';
import { ProductName } from '../components/product-name';
import { productVariantsSeparator, propertiesSeparator } from '../meta-data';
import { Props } from '../types';
import { ThumbSwiper } from './thumb-swiper';

const navigationStyling: SliderButtonStyle = {
  fillMode: 'outlined-white',
  color: 'dark-coal',
};
const navigationHoverStyling: SliderButtonStyle = {
  fillMode: 'outlined-white',
  color: 'light-red',
};

const VariantsSlider = ({
  data,
  activeIndex,
  onSelectionChange,
  slim = false,
  className = '',
  variantsCountText,
}: {
  data: Props;
  activeIndex: number;
  onSelectionChange: (newIndex: number) => void;
  slim?: boolean;
  className?: string;
  variantsCountText: string;
}) => {
  const paginationContainer = useRef<HTMLDivElement>(null);

  const [variantSwiper, setVariantSwiper] = useState<SwiperClass | null>(null);
  const [thumbSwiper, setThumbSwiper] = useState<SwiperClass | null>(null);

  const variants = data.pimProduct.variants;
  const variantsCount = variants.length;

  const onSlideChange = (swiper: SwiperClass) => {
    onSelectionChange(swiper.activeIndex % variants.length);
  };

  const maxThumbCount = slim ? 3 : 7;
  let thumbCount =
    variantsCount >= maxThumbCount ? maxThumbCount : variantsCount;

  // Make sure that the thumbnail count is odd, so we can display the same amount
  // of thumbnails to the left and right of the center (selected) thumbnail.
  if (thumbCount % 2 === 0) {
    // When the thumb count is 2 we increase to 3 to make sure the initial
    // thumb is center aligned and the second thumb is to the right.
    // Otherwise, we decrease the thumb count to make sure we don't display the
    // same repeated thumbnail on the left and right edge.
    thumbCount += thumbCount === 2 ? 1 : -1;
  }

  const disabledPrevButton = activeIndex === 0;
  const disabledNextButton = activeIndex >= variantsCount - 1;
  const isClothingServiceSegment = isClothing(data.pimProduct.serviceSegment);

  const variantsMetaData = variants
    .map((v) =>
      [v.assetId, v.colorName, v.hash, v.slug].join(propertiesSeparator),
    )
    .join(productVariantsSeparator);

  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const labels = getARIATranslations();

  useEffect(() => {
    if (variantSwiper) {
      variantSwiper.slideTo(activeIndex, 0, false);
    }
  }, [activeIndex, variantSwiper]);

  return (
    <div
      className={className}
      data-pagefind-meta={`product_variants:${variantsMetaData}`}
    >
      <SwiperHeader
        slim={slim}
        displayName={data.pimProduct.displayName}
        collectionName={data.pimProduct.collection?.name}
        modelName={data.pimProduct.modelName}
      />
      <HeroSwiperWrapper slim={slim}>
        <Swiper
          modules={[Pagination, Controller, Navigation]}
          pagination={{
            type: 'bullets',
            dynamicBullets: true,
            dynamicMainBullets: 1,
            clickable: false,
            el: paginationContainer.current,
          }}
          controller={{ control: thumbSwiper }}
          slidesPerView={slim ? 1 : 3}
          centeredSlides={true}
          spaceBetween={0}
          initialSlide={activeIndex}
          roundLengths
          loopAdditionalSlides={3}
          onSlideChange={onSlideChange}
          onSwiper={setVariantSwiper}
          allowTouchMove={true}
          navigation={{
            prevEl: prevButtonRef.current,
            nextEl: nextButtonRef.current,
          }}
        >
          {variants.map((variant, index) => (
            <SwiperSlide key={variant.name}>
              <VariantSlideContent
                intendedForSearchIndex={index === 0}
                variant={variant}
                useMbsFallbackIcon={isClothingServiceSegment}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {!slim && variantsCount > 1 && (
          <>
            <LeftNavigationButton
              style={{ opacity: disabledPrevButton ? 0 : 1 }}
            >
              <SliderButtonPrev
                disabled={disabledPrevButton}
                styling={navigationStyling}
                hoverStyling={navigationHoverStyling}
                ref={prevButtonRef}
                ariaLabel={labels.label_back}
              />
            </LeftNavigationButton>
            <RightNavigationButton
              style={{ opacity: disabledNextButton ? 0 : 1 }}
            >
              <SliderButtonNext
                disabled={disabledNextButton}
                styling={navigationStyling}
                hoverStyling={navigationHoverStyling}
                ref={nextButtonRef}
                ariaLabel={labels.label_next}
              />
            </RightNavigationButton>
          </>
        )}
      </HeroSwiperWrapper>
      <MobileGradientWrapper>
        <ActiveSlideInfoWrapper>
          {!slim && variantsCount > 1 && (
            <VariantCountLabel>{variantsCountText}</VariantCountLabel>
          )}

          <ThumbSwiperWrapper
            sx={{
              // 64px thumbnail + 36px margin
              maxWidth: `${thumbCount * 64 + (thumbCount - 1) * 36}px`,
            }}
          >
            <ThumbSwiper
              activeIndex={activeIndex}
              variants={variants}
              setThumbSwiper={setThumbSwiper}
              thumbCount={thumbCount}
              variantSwiper={variantSwiper}
              useMbsFallbackIcon={isClothingServiceSegment}
            />
          </ThumbSwiperWrapper>
          <DesktopGradientWrapper slim={slim}>
            <ColorText
              text={data.pimProduct.variants[activeIndex]?.colorName ?? ''}
            />
          </DesktopGradientWrapper>
        </ActiveSlideInfoWrapper>
        <PaginationWrapper>
          <div ref={paginationContainer}></div>
        </PaginationWrapper>
      </MobileGradientWrapper>
    </div>
  );
};

export default VariantsSlider;

const SwiperHeader = styled(ProductName)(
  ({ slim }: { slim: boolean }) =>
    ({ theme }) => ({
      display: slim ? 'none' : 'flex',
      flexDirection: 'column-reverse',
      marginTop: '65px',
      marginBottom: '45px',

      'h1, h2': {
        margin: 0,
        textAlign: 'center',
      },

      [theme.breakpoints.down('md')]: {
        display: 'flex',
      },

      [theme.breakpoints.down('sm')]: {
        marginTop: '35px',
        marginBottom: '30px',
      },

      h1: {
        ...theme.typography.h2,
        fontWeight: theme.typography.fontWeightMedium,

        [theme.breakpoints.down('sm')]: {
          fontSize: theme.typography.h6.fontSize,
          lineHeight: theme.typography.h6.lineHeight,
        },
      },

      h2: {
        ...theme.typography.body,
        fontWeight: theme.typography.fontWeightRegular,
      },
    }),
);

const MobileGradientWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    paddingBottom: '40px',
    background:
      'linear-gradient(180deg, rgba(243, 243, 243, 0) 24.38%, #F3F3F3 100%)',
  },
}));

const DesktopGradientWrapper = styled('div')(
  ({ slim }: { slim: boolean }) =>
    ({ theme }) => ({
      [theme.breakpoints.up('md')]: {
        ...(slim
          ? {}
          : {
              paddingTop: '17px',
              paddingBottom: '67px',
              background:
                'linear-gradient(180deg, rgba(243, 243, 243, 0) 24.38%, #F3F3F3 100%)',
            }),
      },
    }),
);

const HeroSwiperWrapper = styled('div')(
  ({ slim }: { slim: boolean }) =>
    ({ theme }) => ({
      overflowX: 'hidden',
      position: 'relative',

      // overflow edge to cut-off left and right slides
      '.swiper': {
        width: slim ? '75%' : '140%',
        left: slim ? 'unset' : '-20%',

        '.swiper-slide': {
          width: slim ? '100%' : '33.33% !important',
          maxWidth: slim ? '100%' : '33.33% !important',
          position: 'relative',
          padding: '0 5%',
        },
      },

      [theme.breakpoints.down('sm')]: {
        '.swiper': {
          width: slim ? '100%' : '160%',
          left: slim ? 'unset' : '-30%',

          '.swiper-slide': {
            padding: '0 2%',
          },
        },
      },
      [theme.breakpoints.up('md')]: {
        marginBottom: '50px',
      },
    }),
);

const ThumbSwiperWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  margin: 'auto',

  '.swiper .swiper-slide': {
    maxWidth: '64px',
    height: '82px',
    maxHeight: '82px',

    transition: 'opacity 0.2s ease',

    '&:not(.swiper-slide-active)': {
      cursor: 'pointer',

      '&:hover': {
        opacity: 0.7,
      },
    },
  },

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ActiveSlideInfoWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column-reverse',
  },
}));

const PaginationWrapper = styled('div')(({ theme }) => ({
  marginTop: '35px',

  [theme.breakpoints.up('md')]: {
    display: 'none',
  },

  position: 'relative',
  '> div': {
    position: 'relative',
    '&.swiper-pagination-bullets.swiper-pagination-horizontal': {
      bottom: 0,
    },

    '.swiper-pagination-bullet': {
      backgroundColor: 'var(--color-dark-coal)',
      opacity: 0.4,

      '&.swiper-pagination-bullet-active': {
        opacity: 0.8,
      },
    },
  },
}));

const VariantCountLabel = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  marginTop: '0',
  marginBottom: '23px',
  textAlign: 'center',

  [theme.breakpoints.down('md')]: {
    margin: 0,
  },
}));

const navButtonContainerBase = (theme: Theme): object => ({
  position: 'absolute',
  top: '50%',
  marginTop: '-35px',
  zIndex: 1,
  transition: 'opacity var(--transition-duration) var(--transition-timing)',

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
});

const LeftNavigationButton = styled('div')(({ theme }) => ({
  ...navButtonContainerBase(theme),
  left: '25%',
}));

const RightNavigationButton = styled('div')(({ theme }) => ({
  ...navButtonContainerBase(theme),
  right: '25%',
}));

const ColorText = styled(FadeText)(({ theme }) => ({
  ...theme.typography.footnote,
  fontWeight: theme.typography.fontWeightMedium,
  textAlign: 'center',
  marginTop: '17px',

  [theme.breakpoints.up('md')]: {
    marginTop: '0',
  },
}));

const VariantSlideContent = React.memo(
  ({
    intendedForSearchIndex,
    variant,
    useMbsFallbackIcon,
  }: {
    intendedForSearchIndex: boolean;
    variant: Props['pimProduct']['variants'][0];
    useMbsFallbackIcon: boolean;
  }) => {
    const imgData = [
      slideImg(variant.assetId ?? '', 400, '(max-width: 899px)'),
      slideImg(
        variant.assetId ?? '',
        600,
        '(min-width: 900px) and (max-width: 1199px)',
      ),
      slideImg(variant.assetId ?? '', 1000, '(min-width: 1200px)'),
    ];

    return (
      <SlideImageWrapper>
        {variant.assetId ? (
          <Image
            images={imgData}
            alt={variant.colorName ?? ''}
            intendedForSearchIndex={intendedForSearchIndex}
          />
        ) : (
          <ProductFallbackImage useMbsFallbackIcon={useMbsFallbackIcon} />
        )}
      </SlideImageWrapper>
    );
  },
);

const SlideImageWrapper = styled('div')({
  img: {
    width: '100%',
    height: 'auto',
  },
});

const slideImg = (assetId: string, size: number, media = '') => ({
  src: jpgPath(assetId, size, size),
  media: media,
  width: size,
  height: size,
});
